import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Material
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import InputBase from "@material-ui/core/InputBase";

// Actions
import { getCases } from "../../state/cases/actions";

// Config
import { useCasesPage } from "../../context/CasesPageProvider";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useEmergencyContacts from "../../hooks/useEmergencyContacts";
import useTranslate from "../../hooks/useTranslate";

// Assets
import searchIcon from "../../assets/icons/search.svg";

// Components
import LoadingSection from "../../components/LoadingSection";
import EmergencyBanner from "../../components/EmergencyBanner/index";
import CasesPageContent from "./CasesPageContent";
import CasesPageLoading from "./CasesPageLoading";

const useStyles = makeStyles(theme => ({
  root: {
    ...styles.mixins.mainSection,
    display: "flex",
    flexDirection: "column",
    transition: "padding-top .3s ease-in-out",
    paddingTop: bannerStatus => styles.getSectionTop(bannerStatus)
  },
  section: styles.mixins.sectionContainer,
  search: {
    padding: theme.spacing(4, 0)
  },
  searchInput: {
    backgroundColor: colors.white,
    padding: theme.spacing(1, 3),
    width: "100%",
    borderRadius: theme.shape.borderRadius,
    border: styles.values.border,
    fontSize: 15
  },
  icon: {
    marginRight: theme.spacing(2)
  },
  progress: {
    padding: theme.spacing(4, 0),
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  }
}));

const filterContent = (data, search = "") => {
  if (search === "") return data;
  const filteredData = [];

  const lowerSearch = search.toLowerCase();
  if (data) {
    filteredData.push(
      ...data.filter(
        ({ patientName, userCapacity, placeOfExamination, number }) => {
          return (
            (patientName ?? "").toLowerCase().includes(lowerSearch) ||
            (userCapacity ?? "").toLowerCase().includes(lowerSearch) ||
            (placeOfExamination?.name ?? "")
              .toLowerCase()
              .includes(lowerSearch) ||
            (number ?? "").toLowerCase().includes(lowerSearch)
          );
        }
      )
    );
  }
  return filteredData;
};

let timer = 0;
const delay = (fn, ms) => {
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(fn.bind(this, ...args), ms || 0);
  };
};

const CasesPage = () => {
  const [search, setSearch] = useState("");
  const [scroll, setScroll] = useCasesPage();
  const { data, isLoading } = useSelector(state => state.cases);
  const { isEmergencyBannerOpen } = useEmergencyContacts();
  const classes = useStyles({ isEmergencyBannerOpen });
  const invitations = useSelector(state => state.invitations);
  const getText = useTranslate();
  const { trackException } = useAppInsights();
  const dispatch = useDispatch();

  const content = useMemo(() => {
    const result = {
      drafts: [],
      submitted: [],
      collaborations: []
    };

    if (!data) return result;

    const filteredData = filterContent(data, search);

    filteredData.forEach(item => {
      if (item.isOwnedByUser) {
        if (item.isDraft) result.drafts.push(item);
        else result.submitted.push(item);
      } else {
        result.collaborations.push(item);
      }
    });

    return result;
  }, [data, search]);

  const filteredInvitations = useMemo(
    () => (invitations.data ? filterContent(invitations.data, search) : []),
    [invitations.data, search]
  );

  useEffect(() => {
    // useCases() only fetches once for the whole /cases/* section (see
    // CasesRoutes.jsx), so it never notices a case changing elsewhere (e.g.
    // evidence/incident details updated on its detail page) and this list
    // goes stale. Refresh every time this page itself is shown instead.
    dispatch(getCases(() => trackException("Failed to refresh cases")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let position = scroll;
    let listener = null;

    window.scrollTo(0, position);

    if (typeof window !== "undefined") {
      listener = window.addEventListener("scroll", () => {
        position = window.pageYOffset;
      });
    }

    return () => {
      if (listener) window.removeEventListener("scroll", listener);
      setScroll(position);
    };
  }, []);

  return isLoading || invitations.isLoading ? (
    <main className={classes.root}>
      <CasesPageLoading />
    </main>
  ) : (
    <main className={classes.root}>
      <EmergencyBanner />
      {data && data.length > 0 && (
        <section className={classes.section}>
          <div className={classes.search}>
            <InputBase
              startAdornment={
                <InputAdornment position="start">
                  <img
                    src={searchIcon}
                    className={classes.icon}
                    height={17}
                    alt="search"
                  />
                </InputAdornment>
              }
              onChange={e => delay(setSearch, 500)(e.target.value)}
              className={classes.searchInput}
              placeholder={getText("casesPage.searchPlaceholder")}
            />
          </div>
        </section>
      )}
      <section className={classes.section}>
        <CasesPageContent
          drafts={content.drafts}
          submitted={content.submitted}
          collaborations={content.collaborations}
          pending={filteredInvitations}
          isSearching={!!search}
        />
      </section>
    </main>
  );
};

export default CasesPage;
